import { createFileRoute, notFound, redirect } from "@tanstack/react-router";
import { BLOG_LOCALES, toDbLocale, toParaglideLocale } from "@/lib/blog-schema";
import { getLocale } from "@/paraglide/runtime";
import {
  blogPostLocationQueryOptions,
  blogPostQueryOptions,
} from "@/tools/blog/hooks/use-blog-data";
import { BlogPost } from "@/tools/blog/pages/BlogPost";

const SITE = "https://toolverse.me";

/** Cover images are stored as site-relative paths (`/images/blog/…`). */
function absoluteUrl(pathOrUrl: string): string {
  return /^https?:\/\//.test(pathOrUrl) ? pathOrUrl : `${SITE}${pathOrUrl}`;
}

/** Build a localized blog URL: /<locale?>/blog/<tool>/<topic>/<slug> */
function postUrl(
  localePrefix: string,
  toolSlug: string,
  topicSlug: string,
  slug: string,
): string {
  const prefix = localePrefix === "en" ? "" : `${localePrefix}/`;
  return `${SITE}/${prefix}blog/${toolSlug}/${topicSlug}/${slug}`;
}

export const Route = createFileRoute("/blog/$tool/$topic/$slug")({
  component: BlogPostRoute,
  ssr: true,
  loader: async ({ context, params }) => {
    const locale = getLocale();
    const post = await context.queryClient.ensureQueryData(
      blogPostQueryOptions(params.tool, params.topic, params.slug, locale),
    );
    if (!post) {
      // A past bug emitted MIXED-locale URLs (e.g. /ja/blog/f1-fantasy/strategy/
      // <ja-slug> — English topic segment, Japanese post slug). Google holds
      // ~140 of them as 404s. If the post slug is real for this locale, 301 to
      // its canonical localized URL so link equity and crawl budget recover.
      const loc = await context.queryClient.ensureQueryData(
        blogPostLocationQueryOptions(params.slug, locale),
      );
      if (
        loc &&
        (loc.toolSlug !== params.tool || loc.topicSlug !== params.topic)
      ) {
        throw redirect({
          to: "/blog/$tool/$topic/$slug",
          params: { tool: loc.toolSlug, topic: loc.topicSlug, slug: loc.slug },
          statusCode: 301,
        });
      }
      // Serve a real 404 for unknown slugs — returning 200 with "not found" UI
      // (soft 404) made Google keep crawling garbage URLs like /blog/$tool/$topic/$slug
      throw notFound();
    }
    return { post };
  },
  head: ({ loaderData }) => {
    const post = loaderData?.post;
    if (!post) {
      return { meta: [{ title: "Post Not Found | Toolverse" }] };
    }

    const current = getLocale();
    const curDb = toDbLocale(current);
    const enUrl = postUrl(
      "en",
      String(post.toolSlugs.en ?? post.toolSlug),
      String(post.topicSlugs.en ?? post.topicSlug),
      String(post.slugs.en ?? ""),
    );
    // Self-referencing canonical for the CURRENT locale when this post is
    // actually translated for it; otherwise (English fallback content is being
    // shown) canonical points to the English version to avoid duplicates.
    const hasCurrent = Boolean(
      current !== "en" &&
        post.toolSlugs[curDb] &&
        post.topicSlugs[curDb] &&
        post.slugs[curDb],
    );
    const canonical = hasCurrent
      ? postUrl(
          current,
          String(post.toolSlugs[curDb]),
          String(post.topicSlugs[curDb]),
          String(post.slugs[curDb]),
        )
      : enUrl;

    const meta = [
      { title: `${post.title} | Toolverse` },
      { name: "description", content: post.description },
      { property: "og:title", content: post.title },
      { property: "og:description", content: post.description },
      { property: "og:type", content: "article" },
      { property: "og:url", content: canonical },
      // og:image MUST be absolute — Facebook/LinkedIn/Slack ignore relative
      // image URLs, and the root's site-wide og:image would win instead.
      ...(post.coverImage
        ? [{ property: "og:image", content: absoluteUrl(post.coverImage) }]
        : []),
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: post.title },
      { name: "twitter:description", content: post.description },
      ...(post.coverImage
        ? [{ name: "twitter:image", content: absoluteUrl(post.coverImage) }]
        : []),
    ];

    // Canonical + hreflang for every locale that has tool/topic/post slugs.
    const links: Array<Record<string, string>> = [
      { rel: "canonical", href: canonical },
    ];
    for (const l of BLOG_LOCALES) {
      const toolS = post.toolSlugs[l];
      const topicS = post.topicSlugs[l];
      const slugS = post.slugs[l];
      if (toolS && topicS && slugS) {
        links.push({
          rel: "alternate",
          hrefLang: toParaglideLocale(l),
          href: postUrl(toParaglideLocale(l), toolS, topicS, slugS),
        });
      }
    }
    if (post.toolSlugs.en && post.topicSlugs.en && post.slugs.en) {
      // x-default must be the SAME URL (the English version) on every locale
      // page of the cluster — pointing it at `canonical` made each locale claim
      // itself as default, which breaks hreflang reciprocity.
      links.push({ rel: "alternate", hrefLang: "x-default", href: enUrl });
    }

    // BreadcrumbList structured data (SEO + AI-citation signal).
    const breadcrumb = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Blog",
          item: `${SITE}/blog`,
        },
        {
          "@type": "ListItem",
          position: 2,
          name: post.toolName,
          item: `${SITE}/blog/${post.toolSlugs.en ?? post.toolSlug}`,
        },
        {
          "@type": "ListItem",
          position: 3,
          name: post.topicName,
          item: `${SITE}/blog/${post.toolSlugs.en ?? post.toolSlug}/${post.topicSlugs.en ?? post.topicSlug}`,
        },
        { "@type": "ListItem", position: 4, name: post.title, item: canonical },
      ],
    };

    // BlogPosting structured data — drives article rich results and is a strong
    // attribution signal for AI engines (ChatGPT / Perplexity / AI Overviews).
    const coverImage = post.coverImage
      ? post.coverImage.startsWith("http")
        ? post.coverImage
        : `${SITE}${post.coverImage}`
      : `${SITE}/og-image.svg`;
    const articleLd = {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "@id": `${canonical}#article`,
      mainEntityOfPage: { "@type": "WebPage", "@id": canonical },
      headline: post.title,
      description: post.description,
      inLanguage: current,
      image: coverImage,
      ...(post.publishedAt ? { datePublished: post.publishedAt } : {}),
      dateModified: post.updatedAt ?? post.publishedAt ?? undefined,
      // Named, consistent author identity — matches the visible byline.
      // Organization (not Person) because "Toolverse Team" is a group.
      author: {
        "@type": "Organization",
        "@id": `${SITE}/about#team`,
        name: "Toolverse Team",
        url: `${SITE}/about`,
      },
      publisher: {
        "@type": "Organization",
        "@id": `${SITE}/#organization`,
        name: "Toolverse",
        logo: {
          "@type": "ImageObject",
          url: `${SITE}/toolverse-icon.png`,
        },
      },
    };

    return {
      meta,
      links,
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(articleLd),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(breadcrumb),
        },
      ],
    };
  },
});

function BlogPostRoute() {
  const { tool, topic, slug } = Route.useParams();
  return <BlogPost tool={tool} topic={topic} slug={slug} />;
}
